import { assert } from '@deqo/foundation';
import st from 'string-template';
import { primitive, uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { Client, ClientManager, Communicator } from '../../../concepts/objects';
import { ActiveObjectWithEvents, resolve } from '../../Container';
import { Participant } from '../../canvas/participants/Participant';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { ToastDefinition, ToastType } from './ToastDefinition';
import { ToastLibrary } from './ToastLibrary';

export interface ToastInstance {
    id: string;
    type: ToastType;
    icon: string;
    body: string;
}

export type ToastContext = Record<string, primitive>;

export type ToastHubStreams = {
    invoke: ToastInstance,
    compile: [string, ToastContext],
};

export class ToastHub extends ActiveObjectWithEvents<ToastHubStreams> {

    @resolve(ClientManager) private clients: ClientManager;
    @resolve(Communicator) private comms: Communicator;
    @resolve(ParticipantManager) private participants: ParticipantManager;
    @resolve(ToastLibrary) private register: ToastLibrary;

    public start(): void {
        this.burden(
            this.comms.recv('toast', this.doRecvToast)
        );
    }

    public invoke(nameOrDef: string | ToastDefinition, context: ToastContext): void {
        if (typeof (nameOrDef) === 'string') {
            const toastDef = this.register.get(nameOrDef);
            assert(!!toastDef, `ToastHub.invoke: ${nameOrDef} is not a registered toast.`);
            this.invoke(toastDef, context);
        }
        else {
            this.events.emit('invoke', this.compile(nameOrDef, context));
        }
    }

    public send(toastName: string, context: ToastContext): void;
    public send(to: string, toastName: string, context: ToastContext): void;
    public send(...input: any[]): void {
        const [to, name, context] = (input.length < 3 ? ['*'].concat(input) : input);
        this.comms.send(to, 'toast', { name, context });
    }

    public invokeAndSend(toastName: string, context: ToastContext): void;
    public invokeAndSend(to: string, toastName: string, context: ToastContext): void;
    public invokeAndSend(...input: any[]): void {
        const [to, name, context] = (input.length < 3 ? ['*'].concat(input) : input);
        this.send(to, name, context);
        this.invoke(name, context);
    }

    public createSenderContextData(userIdOrPtpt: string | Participant) {
        const ptpt = typeof (userIdOrPtpt) === 'string' ? this.participants.get(userIdOrPtpt) : userIdOrPtpt;
        return !ptpt ? {} : {
            'senderName': ptpt?.name ?? '???',
        };
    }

    private createUserContextData() {
        const ptpt = this.participants.get(this.clients.self.userId);
        return {
            'userName': ptpt?.name ?? '???',
            // This will be overriden if compiled via the send route, but on invoke there is no
            // sender so set the sender to the user.
            'senderName': ptpt?.name ?? '???',
        };
    }

    private compile(def: ToastDefinition, context: ToastContext) {
        context = { ...this.createUserContextData(), ...context };
        this.events.emit('compile', [def.name, context]);
        return {
            id: uuid(),
            type: def.type,
            icon: def.icon,
            body: st(def.template, context),
        };
    }

    private doRecvToast = (client: Client, { name, context }: any) => {
        this.invoke(name, Object.assign(context, this.createSenderContextData(client.userId)));
    };
}
